import { Component, ViewChild, ElementRef, ChangeDetectorRef, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

export interface GradientStop {
  id: string;
  color: string;
  position: number; // 0 to 100%
  number: string | number;    // Telemetry value (can be string or number)
  label: string | undefined; // Optional label for the stop
}

// --- STANDALONE UTILITY FUNCTION ---
export function getGradientColor(value: number, stops: GradientStop[]): string {
  // Helper to check if value is numeric
  const isNumeric = (val: string | number): boolean => {
    if (typeof val === 'number') return !isNaN(val);
    return !isNaN(parseFloat(val)) && isFinite(parseFloat(val));
  };
  
  // Helper to parse number values (handles both string and number)
  const parseNum = (val: string | number): number => typeof val === 'string' ? parseFloat(val) : val;
  
  // Filter to only numeric stops and sort by numeric value
  const numericStops = stops.filter(s => isNumeric(s.number));
  if (numericStops.length === 0) return stops[0]?.color || '#000000';
  
  const sorted = [...numericStops].sort((a, b) => parseNum(a.number) - parseNum(b.number));
  const firstNum = parseNum(sorted[0].number);
  const lastNum = parseNum(sorted[sorted.length - 1].number);
  
  if (value <= firstNum) return sorted[0].color;
  if (value >= lastNum) return sorted[sorted.length - 1].color;

  let startStop = sorted[0];
  let endStop = sorted[sorted.length - 1];

  for (let i = 0; i < sorted.length - 1; i++) {
    const currentNum = parseNum(sorted[i].number);
    const nextNum = parseNum(sorted[i + 1].number);
    if (value >= currentNum && value <= nextNum) {
      startStop = sorted[i];
      endStop = sorted[i + 1];
      break;
    }
  }

  const range = parseNum(endStop.number) - parseNum(startStop.number);
  const factor = range === 0 ? 0 : (value - parseNum(startStop.number)) / range;

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const startColor = hexToRgb(startStop.color);
  const endColor = hexToRgb(endStop.color);

  const r = Math.round(startColor.r + (endColor.r - startColor.r) * factor);
  const g = Math.round(startColor.g + (endColor.g - startColor.g) * factor);
  const b = Math.round(startColor.b + (endColor.b - startColor.b) * factor);

  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

@Component({
  selector: 'app-gradient-selector',
  templateUrl: './gradient-selector.component.html',
  styleUrls: ['./gradient-selector.component.css']
})
export class GradientSelectorComponent implements OnChanges {
  @ViewChild('trackRef') trackRef!: ElementRef<HTMLDivElement>;

  // --- Inputs/Outputs for two-way binding ---
  @Input() stops: GradientStop[] = [];
  @Output() stopsChange = new EventEmitter<GradientStop[]>();

  @Input() rangeMin: string | number = -20;
  @Output() rangeMinChange = new EventEmitter<string | number>();

  @Input() rangeMax: string | number = 50;
  @Output() rangeMaxChange = new EventEmitter<string | number>();

  // --- State ---
  selectedStopId: string | null = null;
  isDragging: boolean = false;
  copyStatus: string = 'Copy';

  constructor(private cdr: ChangeDetectorRef) {}

  // Helper to check if value is numeric
  private isNumeric(val: string | number): boolean {
    if (typeof val === 'number') return !isNaN(val);
    if (typeof val === 'string') {
      return !isNaN(parseFloat(val)) && isFinite(parseFloat(val));
    }
    return false;
  }

  // Helper to parse number values (handles both string and number)
  private parseNumber(val: string | number): number {
    return typeof val === 'string' ? parseFloat(val) : val;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Initialize with default stops if none provided
    if (changes['stops'] && (!this.stops || this.stops.length === 0)) {
      // Only calculate numeric default stops if both ranges are numeric
      if (this.isNumeric(this.rangeMin) && this.isNumeric(this.rangeMax)) {
        const min = this.parseNumber(this.rangeMin);
        const max = this.parseNumber(this.rangeMax);
        this.stops = [
          { id: this.generateId(), color: '#0d6efd', position: 0, number: this.rangeMin, label: undefined },
          { id: this.generateId(), color: '#198754', position: 35.7, number: min + (max - min) * 0.357, label: undefined },
          { id: this.generateId(), color: '#ffc107', position: 64.3, number: min + (max - min) * 0.643, label: undefined },
          { id: this.generateId(), color: '#dc3545', position: 100, number: this.rangeMax, label: undefined }
        ];
      } else {
        // If ranges are not numeric, just create stops with position-based values
        this.stops = [
          { id: this.generateId(), color: '#0d6efd', position: 0, number: this.rangeMin, label: undefined },
          { id: this.generateId(), color: '#198754', position: 35.7, number: 35.7, label: undefined },
          { id: this.generateId(), color: '#ffc107', position: 64.3, number: 64.3, label: undefined },
          { id: this.generateId(), color: '#dc3545', position: 100, number: this.rangeMax, label: undefined }
        ];
      }
      this.emitStopsChange();
    }
  }

  // --- Computed properties as getters ---
  get sortedStops(): GradientStop[] {
    return [...this.stops].sort((a, b) => a.position - b.position);
  }

  get gradientString(): string {
    const stopsStr = this.sortedStops
      .map(s => `${s.color} ${s.position}%`)
      .join(', ');
    return `linear-gradient(to right, ${stopsStr})`;
  }

  get activeStop(): GradientStop | null {
    return this.stops.find(s => s.id === this.selectedStopId) || null;
  }

  // --- Helpers & Actions ---
  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private emitStopsChange() {
    this.stopsChange.emit(this.stops);
  }

  private valueToPercent(val: string | number, currentPosition?: number): number {
    // If value is not numeric or range is not numeric, return current position or 0
    if (!this.isNumeric(val) || !this.isNumeric(this.rangeMin) || !this.isNumeric(this.rangeMax)) {
      return currentPosition !== undefined ? currentPosition : 0;
    }
    
    const numVal = this.parseNumber(val);
    const min = this.parseNumber(this.rangeMin);
    const max = this.parseNumber(this.rangeMax);
    if (max === min) return 0;
    const pct = ((numVal - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  private percentToValue(pct: number): string | number {
    // Only calculate numeric value if both min and max are numeric
    if (!this.isNumeric(this.rangeMin) || !this.isNumeric(this.rangeMax)) {
      return pct; // Return percentage as fallback
    }
    
    const min = this.parseNumber(this.rangeMin);
    const max = this.parseNumber(this.rangeMax);
    const val = min + (pct / 100) * (max - min);
    return Number(val.toFixed(1)); 
  }

  updateRangeMin(newMin: string | number) {
    this.rangeMin = newMin;
    this.rangeMinChange.emit(newMin);
    // Only recalculate if both min and max are numeric
    if (this.isNumeric(this.rangeMin) && this.isNumeric(this.rangeMax)) {
      this.recalculatePositionsFromValues();
    }
  }

  updateRangeMax(newMax: string | number) {
    this.rangeMax = newMax;
    this.rangeMaxChange.emit(newMax);
    // Only recalculate if both min and max are numeric
    if (this.isNumeric(this.rangeMin) && this.isNumeric(this.rangeMax)) {
      this.recalculatePositionsFromValues();
    }
  }

  private recalculatePositionsFromValues() {
    this.stops = this.stops.map(s => ({
      ...s,
      // Only recalculate position for numeric values, keep position unchanged for text
      position: this.isNumeric(s.number) ? this.valueToPercent(s.number) : s.position
    }));
    this.emitStopsChange();
  }

  onTrackClick(event: MouseEvent) {
    if (this.isDragging) return;

    const rect = this.trackRef.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = this.percentToValue(percentage);

    const newId = this.generateId();
    
    // Find closest previous color
    const closestPrev = this.sortedStops.slice().reverse().find(s => s.position <= percentage);
    const newColor = closestPrev ? closestPrev.color : '#ffffff';

    this.stops = [
      ...this.stops,
      { id: newId, color: newColor, position: percentage, number: value, label: undefined }
    ];

    this.selectedStopId = newId;
    this.emitStopsChange();
  }

  onStopMouseDown(event: MouseEvent, id: string) {
    event.stopPropagation();
    event.preventDefault();
    
    this.selectedStopId = id;
    this.isDragging = true;

    const moveHandler = (e: MouseEvent) => this.handleDrag(e, id);
    const upHandler = () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
      setTimeout(() => this.isDragging = false, 50);
    };

    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
  }

  private handleDrag(event: MouseEvent, id: string) {
    const rect = this.trackRef.nativeElement.getBoundingClientRect();
    let x = event.clientX - rect.left;
    
    let percentage = (x / rect.width) * 100;
    percentage = Math.max(0, Math.min(100, percentage));
    
    const stop = this.stops.find(s => s.id === id);
    
    // Only update the number value if it's currently numeric
    // If it's text, just update the position
    this.stops = this.stops.map(s => {
      if (s.id === id) {
        if (this.isNumeric(s.number)) {
          const newValue = this.percentToValue(percentage);
          return { ...s, position: percentage, number: newValue };
        } else {
          return { ...s, position: percentage };
        }
      }
      return s;
    });
    this.emitStopsChange();
  }

  updateStopValue(id: string, newValue: string | number) {
    this.stops = this.stops.map(s => {
      if (s.id === id) {
        // If value is numeric, update both number and position
        if (this.isNumeric(newValue)) {
          const newPos = this.valueToPercent(newValue, s.position);
          return { ...s, number: newValue, position: newPos };
        } else {
          // If value is text, only update the number field, keep position
          return { ...s, number: newValue };
        }
      }
      return s;
    });
    this.emitStopsChange();
  }

  updateStopColor(color: string) {
    const id = this.selectedStopId;
    if (!id) return;
    this.stops = this.stops.map(s => 
      s.id === id ? { ...s, color } : s
    );
    this.emitStopsChange();
  }

  deleteStop(id: string) {
    if (this.stops.length <= 2) return;
    this.stops = this.stops.filter(s => s.id !== id);
    this.selectedStopId = null;
    this.emitStopsChange();
  }

  copyToClipboard() {
    const css = `background: ${this.gradientString};`;
    const textarea = document.createElement('textarea');
    textarea.value = css;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);

    this.copyStatus = 'Copied!';
    setTimeout(() => this.copyStatus = 'Copy', 2000);
  }

  updateStopLabel(id: string, newLabel: string | undefined) {
    this.stops = this.stops.map(s => {
      if (s.id === id) {
        return { ...s, label: newLabel };
      }
      return s;
    });
    this.emitStopsChange();
  }
}
